package medicine.components

import antd.card.card
import antd.grid.col
import antd.grid.row
import antd.layout.space
import antd.message.message
import antd.radio.radio
import antd.radio.radioGroup
import antd.typography.paragraph
import antd.typography.title
import com.jingfangpai.common.orm.mapping.Medicine
import kotlinext.js.js
import react.*
import react.dom.div
import medicine.containers.MedicineListPageContext
import react.router.dom.useHistory
import urlBase
import external.pinyin.Pinyin
import kotlinx.browser.document
import kotlinx.html.id
import kotlinx.html.js.onClickFunction
import kotlinx.html.onClick
import react.dom.attrs
import react.dom.jsStyle

external interface MedicineListProps : RProps {

}

val MedicineList = functionalComponent<MedicineListProps> { props ->

    val ctx = useContext(MedicineListPageContext)
    val history = useHistory()

    val (medicineList, setMedicineList) = useState(listOf<Medicine>())
    val (sortType, setSortType) = useState("pinyin") // "pinyin" or "type"

    fun getMedicineList() {
        ctx.getMedicineList({ resp ->
            if (resp.ok()) {
                setMedicineList(resp.data ?: listOf())
            } else {
                console.error("Failed to fetch medicine list:", resp)
                message.error("获取中药列表失败:${resp.message ?: "未知错误"}")
            }
        }, { error ->
            console.error("Exception fetching medicine list:", error)
            console.error("Error stack:", error.stackTraceToString())
            val errorMsg = error.message ?: error.toString() ?: "未知错误"
            message.error("获取中药列表失败:$errorMsg")
        })
    }

    useEffectOnce {
        getMedicineList()
    }

    // Group and sort medicines based on sortType
    val groupedMedicines = when (sortType) {
        "pinyin" -> {
            val grouped = medicineList.groupBy { medicine: Medicine ->
                val name = medicine.name?.getLiteral() ?: ""
                if (name.isNotEmpty()) {
                    try {
                        val spell = Pinyin.getSpell(name, null, null)
                        console.log(spell)
                        if (spell.startsWith("[")) {
                            val spells = spell.replace("[", "").replace("]", "").split(",")
                            spells[0].substring(0, 1).uppercase() // 默认第1个
                        } else {
                            spell.substring(0, 1).uppercase()
                        }
                    } catch (e: Exception) {
                        console.error("Pinyin conversion failed:", e)
                        name.substring(0, 1).uppercase()
                    }
                } else "#"
            }
            grouped.toList().sortedBy { it.first }.toMap().mapValues { it.value.sortedBy { med -> med.name?.getLiteral() ?: "" } }
        }
        "type" -> {
            val grouped = medicineList.groupBy { medicine: Medicine ->
                medicine.type?.getLiteral() ?: "其他"
            }
            grouped.toList().sortedBy { it.first }.toMap().mapValues { it.value.sortedBy { med -> med.name?.getLiteral() ?: "" } }
        }
        else -> mapOf("所有" to medicineList.sortedBy { it.name?.getLiteral() ?: "" })
    }

    div("MedicineList") {
        card {
            attrs {
                title = "中药列表"
            }
            div {
                attrs.jsStyle = js {
                    position = "sticky"
                    top = "0"
                    backgroundColor = "white"
                    zIndex = 1000
                    paddingBottom = "10px"
                }
                radioGroup {
                    attrs {
                        value = sortType
                        onChange = {
                            setSortType(it.target.value as String)
                        }
                    }
                    radio {
                        attrs {
                            value = "pinyin"
                        }
                        +"按拼音排序"
                    }
                    radio {
                        attrs {
                            value = "type"
                        }
                        +"按类型排序"
                    }
                }
                // Index bar
                div {
                    attrs.jsStyle = js {
                        display = "flex"
                        flexWrap = "wrap"
                        gap = "5px"
                        marginTop = "10px"
                    }
                    groupedMedicines.keys.sorted().forEach { key ->
                        div {
                            this.attrs {
                                jsStyle = js {
                                    cursor = "pointer"
                                    padding = "5px 10px"
                                    backgroundColor = "#f0f0f0"
                                    borderRadius = "4px"
                                    fontSize = "14px"
                                }
                                onClickFunction = {
                                    val element = document.getElementById("group-$key")
                                    element?.scrollIntoView(js {
                                        behavior = "smooth"
                                        block = "start"
                                    })
                                }
                            }
                            +key
                        }
                    }
                }
            }
            space {
                attrs {
                    size = "middle"
                    direction = "vertical"
                }
            }
            if (medicineList.isEmpty()) {
                div {
                    +"暂无数据"
                }
            } else {
                groupedMedicines.forEach { (groupKey, medicines) ->
                    div {
                        attrs {
                            id = "group-$groupKey"
                            jsStyle = js {
                                marginBottom = "20px"
                            }
                        }
                        title {
                            attrs {
                                level = 4
                            }
                            +groupKey
                        }
                        row {
                            medicines.forEach { medicine ->
                                col {
                                    attrs {
                                        span = 6
                                    }
                                    card {
                                        attrs{
                                            hoverable = true
                                            onClick = {
                                                if (!medicine.id.isNullOrBlank()) {
                                                    history.push("$urlBase/learning/medicine/detail/${medicine.id}")
                                                }
                                            }
                                        }
                                        div {
                                            // 中药名称
                                            title {
                                                attrs {
                                                    level = 4
                                                }
                                                +(medicine.name?.getLiteral() ?: "")
                                            }
                                            // 类型
                                            if (medicine.type != null) {
                                                div {
                                                    +"类型：${medicine.type?.getLiteral()}"
                                                }
                                            }
                                            // 描述
                                            if (!medicine.description.isNullOrBlank()) {
                                                div {
                                                    +"描述："
                                                    paragraph {
                                                        attrs {
                                                            ellipsis = true
                                                        }
                                                        +(medicine.description ?: "")
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
